import React from 'react';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';
import View from './View';
import schema from './schema';
import './style.css';

const updateButtonLabelIfEmpty = (buttons) => {
  return buttons.map((btn) => {
    const link = Array.isArray(btn.link) ? btn.link[0] : btn.link;
    const label = btn.label?.trim();
    if (!label && link && (link.title || link['@id'])) {
      return {
        ...btn,
        label: link.title || link['@id'].split('/').filter(Boolean).pop(),
      };
    }
    return btn;
  });
};

const Edit = (props) => {
  const { data, block, onChangeBlock, selected } = props;

  // Backward compatibility: migrate old root-level button/toc fields if they exist
  React.useEffect(() => {
    if (
      (data.buttons || data.useTOC) && 
      !data.hasOwnProperty('title') && 
      !data.hasOwnProperty('backgroundImage')
    ) {
      const migratedData = {
        ...data,
        title: data.title,
        subtitle: data.subtitle,
        backgroundImage: data.backgroundImage,
        backgroundVideo: data.backgroundVideo,
        buttons: data.buttons,
        alignment: data.alignment,
        useTOC: data.useTOC,
        useH2: data.useH2,
        useH3: data.useH3,
        smallButtons: data.smallButtons,
      };
      onChangeBlock(block, migratedData);
    }
  }, []);

  return (
    <>
      <View {...props} />

      {selected && (
        <SidebarPortal selected={selected}>
          <BlockDataForm
            schema={{
              ...schema(props),
              fieldsets: schema(props).fieldsets
                .map((fs) => {
                  let fields = [...fs.fields];

                  // Handle default fieldset conditional fields
                  if (fs.id === 'default') {
                    // Hide multipleHeroes if carousel is enabled
                    if (data.carouselEnabled) {
                      fields = fields.filter((field) => field !== 'multipleHeroes');
                    }
                    // Hide displayMode if carousel is enabled
                    if (data.carouselEnabled) {
                      fields = fields.filter((field) => field !== 'displayMode');
                    }
                    // Hide carouselDisplayMode if carousel is not enabled
                    if (!data.carouselEnabled) {
                      fields = fields.filter((field) => field !== 'carouselDisplayMode');
                    }
                    // Hide layoutMode if carousel is enabled or multiple heroes is enabled
                    if (data.carouselEnabled || data.multipleHeroes) {
                      fields = fields.filter((field) => field !== 'layoutMode');
                    }
                  }

                  // Handle firstHero fieldset conditional fields
                  if (fs.id === 'firstHero') {
                    // Hide title if using page title
                    if (data.usePageTitle) {
                      fields = fields.filter((field) => field !== 'title');
                    }
                    // Hide subtitle if using page description
                    if (data.usePageDescription) {
                      fields = fields.filter((field) => field !== 'subtitle');
                    }
                    // Hide background image/video if using page preview image
                    if (data.usePreviewImage) {
                      fields = fields.filter((field) => field !== 'backgroundImage' && field !== 'backgroundVideo');
                    }
                    // Hide buttons if using TOC
                    if (data.useTOC) {
                      fields = fields.filter((field) => field !== 'buttons');
                    }
                    // Hide preheader if showing publication date
                    if (data.showPublicationDate) {
                      fields = fields.filter((field) => field !== 'preheader');
                    }
                  }

                  // Handle Image Stack fieldset - show unless layoutMode is standard
                  if (fs.id === 'imageStack' && data.layoutMode !== 'imageStackRight' && data.layoutMode !== 'imageStackLeft') {
                    return { ...fs, fields: [] };
                  }

                  // Hide Additional Heroes fieldset if neither multiple heroes nor carousel is enabled
                  if (fs.id === 'heroes' && !data.multipleHeroes && !data.carouselEnabled) {
                    return { ...fs, fields: [] };
                  }

                  // Hide Carousel Settings fieldset if carousel is not enabled
                  if (fs.id === 'carousel' && !data.carouselEnabled) {
                    return { ...fs, fields: [] };
                  }

                  return {
                    ...fs,
                    fields,
                  };
                })
                .filter((fs) => fs.fields.length > 0), // Remove empty fieldsets
            }}
            title="Hero Block"
            onChangeField={(id, value) => {
              const updated = { ...data, [id]: value };
              onChangeBlock(block, updated);
            }}
            onChangeBlock={(id, newData) => {
              const updated = { ...data, ...newData };
              
              // Update button labels for root-level buttons
              if (newData.buttons) {
                updated.buttons = updateButtonLabelIfEmpty(newData.buttons);
              }
              
              // Update button labels for all heroes
              if (newData.heroes) {
                updated.heroes = newData.heroes.map((hero) => {
                  if (hero.buttons) {
                    return {
                      ...hero,
                      buttons: updateButtonLabelIfEmpty(hero.buttons),
                    };
                  }
                  return hero;
                });
              }

              onChangeBlock(id, updated);
            }}
            block={block}
            formData={data}
          />
        </SidebarPortal>
      )}
    </>
  );
};

export default Edit;
